/**
 * CALENDAR SERVICE
 * 
 * Monthly stamp-based calendar system
 * Replaces streak logic with non-punitive calendar
 * Rewards presence, not perfection
 */

import { doc, updateDoc, getDoc, collection, query, where, getDocs, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { User, DailyStamp, MonthlyCalendar, StampType, RewardType, CalendarConfig } from '../types';
import { awardXP, XP_ACTIONS } from './gamificationService';

// Default calendar configuration
const DEFAULT_CONFIG: CalendarConfig = {
  xpPerStamp: {
    standard: 20,
    special: 50,
    bonus: 100
  },
  milestones: [
    { day: 5, reward: { type: 'xp_boost', value: 50 } },
    { day: 10, reward: { type: 'cosmetic', value: 'frame_basic' } },
    { day: 20, reward: { type: 'discount', value: 0.1 } }, // 10% discount
    { day: 30, reward: { type: 'unlockable', value: 'badge_monthly_complete' } }
  ],
  bonusChance: 0.1 // 10% chance of bonus stamp
};

/**
 * Get current month key (YYYY-MM format)
 */
export const getCurrentMonthKey = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * Get today's date key (YYYY-MM-DD format)
 */
export const getTodayKey = (): string => {
  return new Date().toISOString().split('T')[0];
};

/**
 * Get or create current month calendar for user
 */
export const getCurrentCalendar = async (userId: string): Promise<MonthlyCalendar> => {
  const userRef = doc(db, "users", userId);
  const userSnap = await getDoc(userRef);
  
  if (!userSnap.exists()) {
    throw new Error('User not found');
  }
  
  const user = userSnap.data() as User;
  const currentMonthKey = getCurrentMonthKey();
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  
  // Check if user has calendars
  if (!user.monthlyCalendars || user.monthlyCalendars.length === 0) {
    // Create first calendar
    const newCalendar: MonthlyCalendar = {
      year,
      month,
      stamps: [],
      totalStamps: 0,
      milestonesReached: [],
      rewardsEarned: [],
      archived: false
    };
    
    await updateDoc(userRef, {
      monthlyCalendars: [newCalendar],
      currentCalendarMonth: currentMonthKey
    });
    
    return newCalendar;
  }
  
  // Find current month calendar
  const currentCalendar = user.monthlyCalendars.find(
    cal => cal.year === year && cal.month === month && !cal.archived
  );
  
  if (currentCalendar) {
    return currentCalendar;
  }
  
  // Archive previous month and create new one
  const updatedCalendars = user.monthlyCalendars.map(cal => {
    if (!cal.archived && cal.year === year && cal.month === month - 1) {
      return { ...cal, archived: true };
    }
    return cal;
  });
  
  const newCalendar: MonthlyCalendar = {
    year,
    month,
    stamps: [],
    totalStamps: 0,
    milestonesReached: [],
    rewardsEarned: [],
    archived: false
  };
  
  updatedCalendars.push(newCalendar);
  
  await updateDoc(userRef, {
    monthlyCalendars: updatedCalendars,
    currentCalendarMonth: currentMonthKey
  });
  
  return newCalendar;
};

/**
 * Add daily stamp to calendar
 * Called when user opens app or completes daily action
 */
export const addDailyStamp = async (
  userId: string,
  stampType: StampType = 'standard',
  reflection?: string
): Promise<{ stamp: DailyStamp; xpEarned: number; milestoneReached?: number }> => {
  const userRef = doc(db, "users", userId);
  const userSnap = await getDoc(userRef);
  
  if (!userSnap.exists()) {
    throw new Error('User not found');
  }
  
  const user = userSnap.data() as User;
  const today = getTodayKey();
  const calendar = await getCurrentCalendar(userId);
  const config = user.calendarConfig || DEFAULT_CONFIG;
  
  // Check if stamp already exists for today
  const existingStamp = calendar.stamps.find(s => s.date === today);
  if (existingStamp) {
    // Already stamped today, return existing
    return {
      stamp: existingStamp,
      xpEarned: existingStamp.xpEarned
    };
  }
  
  // Determine XP based on stamp type
  let xpEarned = config.xpPerStamp[stampType];
  
  // Check for bonus (randomized but fair)
  let finalType = stampType;
  let bonus = undefined;
  if (stampType === 'standard' && Math.random() < config.bonusChance) {
    finalType = 'bonus';
    xpEarned = config.xpPerStamp.bonus;
    bonus = {
      type: 'lucky_stamp',
      value: xpEarned - config.xpPerStamp.standard
    };
  }
  
  // Create new stamp (only include optional fields if they exist - Firestore doesn't accept undefined)
  const newStamp: DailyStamp = {
    date: today,
    type: finalType,
    xpEarned
  };
  
  // Only add optional fields if they have values
  if (reflection) {
    newStamp.reflection = reflection;
  }
  if (bonus) {
    newStamp.bonus = bonus;
  }
  
  // Add stamp to calendar
  const updatedStamps = [...calendar.stamps, newStamp];
  const totalStamps = updatedStamps.length;
  
  // Check milestones
  let milestoneReached: number | undefined;
  const updatedMilestones = [...calendar.milestonesReached];
  const milestone = config.milestones.find(m => m.day === totalStamps && !updatedMilestones.includes(m.day));
  
  if (milestone) {
    milestoneReached = milestone.day;
    updatedMilestones.push(milestone.day);
    
    // Add reward (only include expiresAt if it's a discount type)
    const reward: any = {
      type: milestone.reward.type,
      value: milestone.reward.value,
      unlockedAt: today,
      used: false
    };
    
    // Only add expiresAt for discount rewards (Firestore doesn't accept undefined)
    if (milestone.reward.type === 'discount') {
      reward.expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(); // 30 days for discounts
    }
    
    calendar.rewardsEarned.push(reward);
  }
  
  // Update calendar
  const updatedCalendar: MonthlyCalendar = {
    ...calendar,
    stamps: updatedStamps,
    totalStamps,
    milestonesReached: updatedMilestones
  };
  
  // Update user document
  const userCalendars = user.monthlyCalendars || [];
  const calendarIndex = userCalendars.findIndex(
    cal => cal.year === calendar.year && cal.month === calendar.month
  );
  
  if (calendarIndex >= 0) {
    userCalendars[calendarIndex] = updatedCalendar;
  } else {
    userCalendars.push(updatedCalendar);
  }
  
  // Prepare update data (only include fields that exist - Firestore doesn't accept undefined)
  const updateData: any = {
    monthlyCalendars: userCalendars
  };
  
  // Only update currentCalendarMonth if it's the current month
  const currentMonthKey = getCurrentMonthKey();
  const calendarMonthKey = `${calendar.year}-${String(calendar.month).padStart(2, '0')}`;
  if (calendarMonthKey === currentMonthKey) {
    updateData.currentCalendarMonth = currentMonthKey;
  }
  
  await updateDoc(userRef, updateData);
  
  // Award XP
  await awardXP(userId, xpEarned, 'daily_stamp');
  
  // Only log in development - production console should be clean
  if (import.meta.env.DEV) {
    console.log(`[CALENDAR] Added stamp for user ${userId}: ${finalType}, ${xpEarned} XP`);
  }
  
  return {
    stamp: newStamp,
    xpEarned,
    milestoneReached
  };
};

/**
 * Get calendar statistics for current month
 */
export const getCalendarStats = (calendar: MonthlyCalendar) => {
  const totalDays = new Date(calendar.year, calendar.month, 0).getDate();
  const completionPercent = (calendar.totalStamps / totalDays) * 100;
  
  return {
    totalStamps: calendar.totalStamps,
    totalDays,
    completionPercent,
    milestonesReached: calendar.milestonesReached.length,
    rewardsEarned: calendar.rewardsEarned.length,
    rewardsUnused: calendar.rewardsEarned.filter(r => !r.used).length
  };
};

/**
 * Use a reward (mark as used)
 */
export const useReward = async (
  userId: string,
  rewardIndex: number
): Promise<void> => {
  const userRef = doc(db, "users", userId);
  const userSnap = await getDoc(userRef);
  
  if (!userSnap.exists()) {
    throw new Error('User not found');
  }
  
  const user = userSnap.data() as User;
  const calendar = await getCurrentCalendar(userId);
  
  if (rewardIndex >= calendar.rewardsEarned.length) {
    throw new Error('Invalid reward index');
  }
  
  const reward = calendar.rewardsEarned[rewardIndex];
  if (reward.used) {
    throw new Error('Reward already used');
  }
  
  // Mark reward as used
  const updatedRewards = [...calendar.rewardsEarned];
  updatedRewards[rewardIndex] = { ...reward, used: true };
  
  const updatedCalendar: MonthlyCalendar = {
    ...calendar,
    rewardsEarned: updatedRewards
  };
  
  // Update user document
  const userCalendars = user.monthlyCalendars || [];
  const calendarIndex = userCalendars.findIndex(
    cal => cal.year === calendar.year && cal.month === calendar.month
  );
  
  if (calendarIndex >= 0) {
    userCalendars[calendarIndex] = updatedCalendar;
  }
  
  await updateDoc(userRef, {
    monthlyCalendars: userCalendars
  });
  
  // Only log in development - production console should be clean
  if (import.meta.env.DEV) {
    console.log(`[CALENDAR] Used reward ${rewardIndex} for user ${userId}`);
  }
};

/**
 * Get archived calendars (past months)
 */
export const getArchivedCalendars = (user: User): MonthlyCalendar[] => {
  return (user.monthlyCalendars || []).filter(cal => cal.archived);
};

/**
 * Check if user has stamped today
 */
export const hasStampedToday = (calendar: MonthlyCalendar): boolean => {
  const today = getTodayKey();
  return calendar.stamps.some(s => s.date === today);
};

